import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { useSellerProfile, isPendingSellerProfile } from '../../hooks/useSellerProfile';
import SellerDashboard from '../SellerDashboard/SellerDashboard';
import BuyerDashboard from '../BuyerDashboard/BuyerDashboard';
import SellerPendingApproval from '../SellerPendingApproval/SellerPendingApproval';
import { DashboardRoleLoadingView } from './Dashboard.view';

/**
 * `/dashboard`, the role router. Container only: it decides WHICH dashboard,
 * and owns nothing else.
 *
 * BEHAVIOUR IS UNCHANGED. Same `useSellerProfile({ enabled: !!user && !isSeller })`
 * call, same `enabled` guard, same three-way outcome, same redirect for a signed
 * out visitor. No request, no query key and no param moved.
 *
 * ONE FIX: the redirect ran during render (`navigate('/login')` in the body,
 * then `return null`). React 18 warns about that and it can fire twice under
 * StrictMode. It is an effect now, and the render path returns the same nothing
 * it always did while the redirect settles.
 *
 * WHAT DIED: `Dashboard.css`, and the `LoadingSpinner` on an empty page. The
 * loading state is a real screen now, see `Dashboard.view.jsx`.
 */
const Dashboard = () => {
  const { user, isSeller } = useAuth();
  const navigate = useNavigate();

  // Approved sellers skip this; for everyone else we check whether they have a
  // seller application still awaiting approval (role hasn't flipped to "seller").
  const { data: sellerProfile, isLoading } = useSellerProfile({
    enabled: !!user && !isSeller,
  });

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  if (!user) return null;

  if (isSeller) {
    return <SellerDashboard />;
  }

  if (isLoading) {
    return <DashboardRoleLoadingView />;
  }

  if (isPendingSellerProfile(sellerProfile)) {
    return <SellerPendingApproval storeName={sellerProfile.store_name} />;
  }

  return <BuyerDashboard />;
};

export default Dashboard;
